import React from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion } from 'framer-motion';
import { ArrowRight, GitCompareArrows } from 'lucide-react';
export function CrossSpecialtyComparison({
  state,
  nextStep
}: StepProps) {
  const specialties = [{
    id: 'Nephrologist',
    color: SPECIALTY_COLORS.Nephrologist
  }, {
    id: 'Cardiologist',
    color: SPECIALTY_COLORS.Cardiologist
  }, {
    id: 'Endocrinologist',
    color: SPECIALTY_COLORS.Endocrinologist
  }, {
    id: 'PCP',
    color: SPECIALTY_COLORS.PCP
  }];
  const rows = [{
    label: 'Proactivity',
    Nephrologist: 'Late',
    Cardiologist: 'Early',
    Endocrinologist: 'Mid',
    PCP: 'Late'
  }, {
    label: 'Primary Driver',
    Nephrologist: 'Safety Data',
    Cardiologist: 'Outcomes',
    Endocrinologist: 'Guidelines',
    PCP: 'Peer Behavior'
  }, {
    label: 'Key Barrier',
    Nephrologist: 'Late Referrals',
    Cardiologist: 'Workload',
    Endocrinologist: 'Guidance',
    PCP: 'Guidance'
  }, {
    label: 'Treatment Approach',
    Nephrologist: 'Cautious / Reactive',
    Cardiologist: 'Aggressive / Proactive',
    Endocrinologist: 'Metabolic Focus',
    PCP: 'Conservative'
  }, {
    label: 'Openness to New MoAs',
    Nephrologist: 'High (if safe)',
    Cardiologist: 'High (if CV benefit)',
    Endocrinologist: 'Moderate',
    PCP: 'Low'
  }];
  // Calculate perception accuracy if they used perception mode
  let accuracyScore = 0;
  if (state.perceptionMode) {
    const total = 4;
    let correct = 0;
    if (state.perceptionNeph === 'Late / Cautious') correct++;
    if (state.perceptionCardio === 'Early / Proactive') correct++;
    if (state.perceptionEndo === 'Balanced') correct++;
    if (state.perceptionPCP === 'Late / Cautious') correct++;
    accuracyScore = Math.round(correct / total * 100);
  }
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto bg-gray-50" data-id="element-222">
      <div className="max-w-6xl w-full mx-auto" data-id="element-223">
        <div className="text-center mb-12" data-id="element-224">
          <div className="inline-flex items-center justify-center p-3 bg-dark text-white rounded-full mb-6" data-id="element-225">
            <GitCompareArrows size={32} data-id="element-226" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-dark mb-4" data-id="element-227">
            Cross-Specialty Comparison
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-228">
            Specialties operate with fundamentally different priorities.
          </p>
        </div>

        {state.perceptionMode && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8 max-w-2xl mx-auto text-center flex items-center justify-between" data-id="element-229">
            <div className="text-left" data-id="element-230">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-231">
                Your Perception Accuracy
              </h3>
              <p className="text-gray-700" data-id="element-232">
                How well you predicted specialty behavior
              </p>
            </div>
            <div className="text-3xl font-black text-dark" data-id="element-233">
              {accuracyScore}%
            </div>
          </motion.div>}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden mb-10" data-id="element-234">
          <div className="overflow-x-auto" data-id="element-235">
            <table className="w-full text-left border-collapse" data-id="element-236">
              <thead data-id="element-237">
                <tr data-id="element-238">
                  <th className="p-6 bg-gray-50 border-b border-gray-200 w-1/5" data-id="element-239"></th>
                  {specialties.map(spec => <th key={spec.id} className="p-6 border-b border-gray-200 w-1/5 text-center" data-id="element-240">
                      <span className="text-lg font-bold" style={{
                    color: spec.color
                  }} data-id="element-241">
                        {spec.id}
                      </span>
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-242">
                {rows.map((row, i) => <tr key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-243">
                    <td className="p-6 border-b border-gray-100 font-bold text-gray-700 text-sm uppercase tracking-wider" data-id="element-244">
                      {row.label}
                    </td>
                    {specialties.map(spec => <td key={`${row.label}-${spec.id}`} className="p-6 border-b border-gray-100 text-center font-medium text-gray-800" data-id="element-245">
                        {row[spec.id as keyof typeof row]}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div className="bg-dark text-white rounded-2xl p-8 mb-10 text-center shadow-lg max-w-3xl mx-auto" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} data-id="element-246">
          <p className="text-xl font-medium leading-relaxed" data-id="element-247">
            "These differences mean the same message won't work for every
            specialty. Engagement must be tailored to their specific drivers and
            barriers."
          </p>
        </motion.div>

        <div className="flex justify-center pb-8" data-id="element-248">
          <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-brand rounded-full hover:bg-red-700 transition-colors" data-id="element-249">
            See the System Impact
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-250" />
          </motion.button>
        </div>
      </div>
    </div>;
}